import { useState, useRef, type KeyboardEvent } from "react";
import { X as CloseX } from "@untitledui/icons";
import { cx } from "@/utils/cx";

interface TagInputProps {
    value: string[];
    onChange: (tags: string[]) => void;
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
}

export const TagInput = ({ value, onChange, label, placeholder = "Add tag...", disabled, className }: TagInputProps) => {
    const [inputValue, setInputValue] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);

    const addTag = (tag: string) => {
        const trimmed = tag.trim();
        if (trimmed && !value.includes(trimmed)) {
            onChange([...value, trimmed]);
        }
        setInputValue("");
    };

    const removeTag = (index: number) => {
        onChange(value.filter((_, i) => i !== index));
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if ((e.key === "Enter" || e.key === ",") && inputValue.trim()) {
            e.preventDefault();
            addTag(inputValue);
        }
        if (e.key === "Backspace" && !inputValue && value.length > 0) {
            removeTag(value.length - 1);
        }
    };

    return (
        <div className={className}>
            {label && (
                <label className="mb-1.5 block text-sm font-medium text-secondary">
                    {label}
                </label>
            )}
            <div
                className={cx(
                    "flex flex-wrap items-center gap-1.5 rounded-lg border border-primary px-3 py-2 shadow-xs transition-colors",
                    "focus-within:border-brand focus-within:ring-4 focus-within:ring-brand/12",
                    disabled && "cursor-not-allowed opacity-50"
                )}
                onClick={() => inputRef.current?.focus()}
            >
                {value.map((tag, index) => (
                    <span
                        key={`${tag}-${index}`}
                        className="inline-flex items-center gap-1 rounded-md bg-utility-gray-50 px-2 py-0.5 text-xs font-medium text-utility-gray-700 ring-1 ring-inset ring-utility-gray-200"
                    >
                        {tag}
                        {!disabled && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    removeTag(index);
                                }}
                                className="ml-0.5 text-utility-gray-400 hover:text-utility-gray-600"
                            >
                                <CloseX className="size-3" />
                            </button>
                        )}
                    </span>
                ))}
                <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={() => {
                        if (inputValue.trim()) addTag(inputValue);
                    }}
                    placeholder={value.length === 0 ? placeholder : ""}
                    disabled={disabled}
                    className="min-w-[80px] flex-1 border-none bg-transparent text-sm text-primary outline-none placeholder:text-placeholder"
                />
            </div>
        </div>
    );
};
